import { useEffect, useRef } from "react";
import styled, { css } from "styled-components";

import SHADOWS from "components/atoms/shadows";
import COLORS from "components/atoms/colors";
import { TextSm } from "components/atoms/text";

import useDropdown from "hooks/useDropdown";
import iconArrowDown from "assets/images/common/arrow-down.svg";

interface Option {
  name: string;
  value: string;
}

type Shape = "round" | "square";

interface Props {
  options: Option[];
  selectIndex: number;
  onChageSelectIndex: (i: number) => void;
  shape: Shape;
  className?: string;
  active?: boolean;
  initialIndex?: number;
}

const Select = ({
  options,
  selectIndex,
  onChageSelectIndex,
  shape,
  className,
  active,
  initialIndex,
}: Props) => {
  const element = useRef(null);
  const [show, setShow] = useDropdown(element);

  useEffect(() => {
    if (initialIndex) {
      onChageSelectIndex(initialIndex);
    }
  }, [initialIndex]);

  const handleClick = (index: number) => {
    setShow(false);
    onChageSelectIndex(index);
  };

  const current = options[selectIndex];

  return (
    <div
      ref={element}
      className={`relative flex flex-col justify-center ${className}`}
    >
      <BtnSelect
        onClick={() => setShow(!show)}
        active={!!initialIndex || active || show}
        shape={shape}
        type="button"
      >
        {current?.name}
        <img src={iconArrowDown} alt="" />
      </BtnSelect>
      {show && (
        <OptionWrapper>
          {options.map(({ name }, i) => (
            <OptionItem key={name} onClick={() => handleClick(i)}>
              <TextSm>{name}</TextSm>
            </OptionItem>
          ))}
        </OptionWrapper>
      )}
    </div>
  );
};

const BtnSelect = styled.button<{ active: boolean; shape: Shape }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 100px;
  height: 44px;
  padding: 12px;
  border: 1px solid ${COLORS.black["40%"]};
  background: #fff;
  font-size: 14px;
  color: ${COLORS.black["40%"]};

  border-radius: ${({ shape }) =>
    ({
      square: "4px",
      round: "44px",
    }[shape])};

  ${({ active }) =>
    active &&
    css`
      border: 1px solid ${COLORS.black["100%"]};
      color: ${COLORS.black["100%"]};
    `}
`;

const OptionWrapper = styled.ul`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  min-width: 100px;
  width: 100%;
  z-index: 10;
  background: #ffffff;
  box-shadow: ${SHADOWS.card};
  border-radius: 4px;
  overflow: hidden;
`;

const OptionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.white["60%"]};
  }
`;

export default Select;
